import React, { useEffect, useMemo, useRef, useState } from 'react'
import { MicrosoftCloudSettingsSection } from './MicrosoftCloudSettingsSections/MicrosoftCloudSettingsSections'
import { Prerequisites } from './MicrosoftCloudSettingsSections/Prerequisites/Prerequisites'
import { Formik, Form } from 'formik'
import { MicrosoftCloudSettingsSections } from '../../../modules/flyOutOnboarding/types'
import { AccountConfiguration } from '../../FlyOutCloudSettings/AccountConfiguration'
import { useStyles } from './FlyOutMicrosoftCloudSettings.styles'
import {
  ConnectCloudAccount,
  ConnectCloudAccountActions,
  ConnectCloudAccountRef,
} from './MicrosoftCloudSettingsSections/ConnectCloudAccount/ConnectCloudAccount'
import { FlyOutOnboardingFooter } from '../FlyOutOnboardingFooter/FlyOutOnboardingFooter'
import { useDispatch, useSelector } from '../../../store'
import { convertReducerToMicrosoftForm } from '../../../modules/flyOutOnboarding/utils'
import {
  LoadingStateComponent,
  LoadingTypes,
  isLoading,
  useAccountId,
  type UserProfileState,
} from '@labrav/common'
import { getFlyoutMicrosoftOnboardingFormData } from '../../../modules/flyOutOnboarding/action'
import { CloudType } from '../../../types'
import { DateTime } from 'luxon'
import { isEmpty } from 'lodash'
import { footerStateType } from '../../../common/components/FooterV2/FooterV2'
import { autoSaveThresoldTime } from '../../../common/utils/constants'
import { LockMetadata } from '../../../modules/flyOutOnboarding/types'
import {
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
} from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faOctagonExclamation } from '@fortawesome/pro-regular-svg-icons'
import { Typography } from '@mui/material'
import clsx from 'clsx'
import { getFlyoutCloudSettingsValidationSchema } from '../../../common/utils/FlyoutCloudSettingsValidationSchema'
import { useLocation } from 'react-router-dom'

interface FlyOutMicrosoftCloudSettingsProps {
  accountId?: string
}

const FlyOutMicrosoftCloudSettings: React.FC<
  FlyOutMicrosoftCloudSettingsProps
> = ({ accountId: accountIdFromProps }) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const accountIdFromState = useAccountId({
    product: 'flyout',
    cloud: CloudType.AZURE,
  })
  const accountId = accountIdFromProps || accountIdFromState
  const noOps = () => void 0
  const { pathname } = useLocation()
  const isOnboardingRoute = pathname.split('/').includes('onboarding')
  const isInIframe = pathname.split('/').includes('external-apps')
  const { userProfile } = useSelector<UserProfileState>(
    state => state.userProfile
  )

  const microsoftOnboardingData = useSelector(
    state => state.flyOutOnboarding.microsoftOnboardingForm
  )
  const formikInitialValues = convertReducerToMicrosoftForm(
    microsoftOnboardingData
  )
  const isLoadingMicrosoftOnboardingData = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GET_FLYOUT_ONBOARDING_DATA)
  )
  useEffect(() => {
    if (accountId) {
      dispatch(
        getFlyoutMicrosoftOnboardingFormData(accountId, CloudType.AZURE, true)
      )
    }
  }, [dispatch, accountId])

  const [lockStatus, setLockStatus] = useState('')
  const [startEditTime, setStartEditTime] = useState<string | undefined>()
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false)
  const [expandedSections, setExpandedSections] = useState<{
    prerequisites: boolean
    accountConfiguration: boolean
    cloudCredentials: boolean
  }>({
    prerequisites: true,
    accountConfiguration: false,
    cloudCredentials: false,
  })
  const connectCloudAccountRef = useRef<ConnectCloudAccountRef>(null)

  useEffect(() => {
    if (microsoftOnboardingData.lockMetadata) {
      const lockMetadata = microsoftOnboardingData.lockMetadata
      if (lockMetadata) {
        if (
          lockMetadata.lockedBy &&
          lockMetadata.lockedBy === userProfile.id &&
          lockMetadata.windowId === window.name
        ) {
          setLockStatus('')
        }
      }
    }
  }, [microsoftOnboardingData.lockMetadata])

  useEffect(() => {
    if (lockStatus === '') {
      setStartEditTime(DateTime.now().toUTC().toISO())
    } else {
      setStartEditTime(undefined)
    }
  }, [lockStatus])

  useEffect(() => {
    if (!isEmpty(microsoftOnboardingData.lockMetadata)) {
      const lockMetadata = microsoftOnboardingData.lockMetadata
      if (lockMetadata) {
        const timeDiffnew =
          Math.ceil(
            DateTime.now().diff(
              startEditTime ? DateTime.fromISO(startEditTime) : DateTime.now(),
              ['seconds']
            ).seconds
          ) > autoSaveThresoldTime
        if (lockMetadata.lockedBy && lockMetadata.lockedBy !== userProfile.id) {
          if (lockStatus === '' && timeDiffnew) {
            setIsOpenModal(true)
          }
          setLockStatus(footerStateType.LOCKED)
        } else {
          if (lockMetadata.windowId !== window.name) {
            if (lockStatus === '' && timeDiffnew) {
              setIsOpenModal(true)
            }
            setLockStatus(footerStateType.LOCKED)
          }
        }
      }
    }
  }, [
    microsoftOnboardingData.lockMetadata?.lastActivityAt,
    microsoftOnboardingData.lockMetadata,
  ])

  const lastActivityAt = DateTime.now().toUTC().toISO()

  const latestLockMetadata: LockMetadata = {
    windowId: window.name,
    lockedBy: userProfile?.id as string,
    lastActivityAt: lastActivityAt,
    lockedByName:
      `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim(),
    lockByEmail: userProfile.email,
  }

  const isFormLocked = lockStatus !== '' && isOnboardingRoute
  const disableTooltipMsg = isFormLocked
    ? `Unable to edit as ${
        microsoftOnboardingData.lockMetadata?.lockedByName
      } is editing the form right now.`
    : ''

  const validationSchema = useMemo(
    () => getFlyoutCloudSettingsValidationSchema(isInIframe),
    [isInIframe]
  )

  if (isLoadingMicrosoftOnboardingData) {
    return (
      <div className={classes.loadingContainer}>
        <LoadingStateComponent
          heading="Loading your Microsoft settings"
          description="It may take a few moments, please wait..."
        />
      </div>
    )
  }

  return (
    <Formik
      initialValues={formikInitialValues}
      validationSchema={validationSchema}
      validateOnMount={true}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={noOps}
      enableReinitialize
    >
      {formikProps => (
        <Form>
          <div>
            <LabraToolTipV2
              tooltipPlacement="bottom-start"
              description={
                !isOnboardingRoute
                  ? ''
                  : lockStatus === footerStateType.LOCKED
                    ? disableTooltipMsg
                    : lockStatus === footerStateType.UNLOCKED
                      ? 'Click on “Start editing” below to edit the form.'
                      : ''
              }
              tooltipPlacmentGap="32px"
              followCursor={isFormLocked}
            >
              <div className={classes.mainContainer}>
                <div
                  className={clsx(
                    classes.formContainer,
                    isOnboardingRoute && isFormLocked
                      ? classes.formDisabled
                      : ''
                  )}
                >
                  <div
                    className={
                      isOnboardingRoute && isFormLocked
                        ? classes.disableEvents
                        : ''
                    }
                  >
                    <div className={classes.sectionsContainer}>
                      <MicrosoftCloudSettingsSection
                        heading="Prerequisites"
                        children={<Prerequisites isFormLocked={isFormLocked} />}
                        sectionName="prerequisites"
                        expandBox={expandedSections.prerequisites}
                      />
                      {!isInIframe && (
                        <MicrosoftCloudSettingsSection
                          heading="Account Configuration"
                          children={
                            <AccountConfiguration
                              sectionName={
                                MicrosoftCloudSettingsSections.ACCOUNT_CONFIGURATION
                              }
                              isFormLocked={isFormLocked}
                              formik={formikProps}
                            />
                          }
                          sectionName="accountConfiguration"
                          expandBox={expandedSections.accountConfiguration}
                        />
                      )}
                      <MicrosoftCloudSettingsSection
                        heading="Connect your cloud account"
                        isInIframe={isInIframe}
                        children={
                          <ConnectCloudAccount
                            ref={connectCloudAccountRef}
                            isFormLocked={isFormLocked}
                            accountId={accountId}
                          />
                        }
                        sectionName="cloudCredentials"
                        actions={<ConnectCloudAccountActions />}
                        expandBox={expandedSections.cloudCredentials}
                      />
                    </div>
                  </div>
                </div>
                <FlyOutOnboardingFooter
                  isFormLocked={isFormLocked}
                  lockStatus={lockStatus}
                  setLockStatus={setLockStatus}
                  userProfileId={userProfile?.id as string}
                  latestLockMetadata={latestLockMetadata}
                  formikProps={formikProps}
                  disableTooltipMsg={disableTooltipMsg}
                  setExpandedSections={setExpandedSections}
                  accountId={accountId}
                  connectCloudAccountRef={connectCloudAccountRef}
                />
              </div>
            </LabraToolTipV2>
          </div>
          <ModalConfirmV2
            title={'Lock released'}
            content={
              <Typography className={classes.modalText}>
                Your editing session expired due to inactivity, and the form is
                now being edited by{' '}
                <span className={classes.nameBoldText}>
                  {microsoftOnboardingData.lockMetadata?.lockedByName || ''}
                </span>
                . Check back later.
              </Typography>
            }
            open={isOpenModal}
            titleAcceptButton={'Ok'}
            onAccept={() => setIsOpenModal(false)}
            onDecline={() => setIsOpenModal(false)}
            variant={ModalSizeVariants.small}
            acceptDisabled={false}
            hideCancelButton={true}
            icon={
              <FontAwesomeIcon
                icon={faOctagonExclamation}
                style={{ width: '20px', height: '16px' }}
              />
            }
          />
        </Form>
      )}
    </Formik>
  )
}

export { FlyOutMicrosoftCloudSettings }
export default FlyOutMicrosoftCloudSettings
